// HT/FT — flow view (Phase 3, D3 SVG). React owns the container + <svg> skeleton
// (the three groups); D3 owns the ribbons, nodes, labels and column
// headers via keyed joins in a useEffect (a port of the legacy drawFlow), so
// everything animates on a control change. The skeleton persists across control
// changes (nodes/ribbons keyed by outcome), so the first draw snaps and later
// draws animate. ResizeObserver redraws on resize. Same DOM as the legacy render.

import { useEffect, useRef } from "react";
import { select } from "d3-selection";
import "d3-transition";
import { easeCubicInOut } from "d3-ease";
import { HTFT_ORDER, type HtftCountData, type HtftResult } from "@bento/shared";

const COLORS: Record<HtftResult, string> = {
  W: "#2ecc71",
  D: "#888",
  L: "#e74c3c",
};
const DURATION = 500;

interface RibbonDatum {
  key: string;
  ht: HtftResult;
  ft: HtftResult;
  rh: number;
  srcTop: number;
  tgtTop: number;
}

function draw(
  svgEl: SVGSVGElement,
  counts: HtftCountData,
  total: number,
  useTransition: boolean
): void {
  const W = svgEl.clientWidth;
  const H = svgEl.clientHeight;
  if (W < 20 || H < 20) return; // not laid out yet — the ResizeObserver retries

  const { htC, ftC, flows } = counts;
  const mL = 36,
    mR = 36,
    mT = 18,
    mB = 8;
  const cW = W - mL - mR,
    cH = H - mT - mB;
  const nodeW = 10,
    nodeGap = 5;
  const usableH = cH - 2 * nodeGap;
  const lx = mL,
    rx = mL + cW - nodeW,
    midX = mL + cW / 2;
  const dur = useTransition ? DURATION : 0;
  const ease = easeCubicInOut;

  const layout = (c: Record<HtftResult, number>) => {
    let y = mT + nodeGap;
    const map = {} as Record<HtftResult, { y: number; h: number }>;
    HTFT_ORDER.forEach((r) => {
      const h = Math.max(0, (c[r] / total) * usableH);
      map[r] = { y, h };
      y += h + nodeGap;
    });
    return map;
  };
  const LL = layout(htC),
    RL = layout(ftC);

  // ---- Ribbons ----
  const srcOff: Record<HtftResult, number> = { W: 0, D: 0, L: 0 };
  const tgtOff: Record<HtftResult, number> = { W: 0, D: 0, L: 0 };
  const ribbonData: RibbonDatum[] = [];
  HTFT_ORDER.forEach((ht) =>
    HTFT_ORDER.forEach((ft) => {
      const count = flows[`${ht}/${ft}`] || 0;
      if (count === 0) return;
      const rh = (count / total) * usableH;
      ribbonData.push({
        key: `${ht}/${ft}`,
        ht,
        ft,
        rh,
        srcTop: LL[ht].y + srcOff[ht],
        tgtTop: RL[ft].y + tgtOff[ft],
      });
      srcOff[ht] += rh;
      tgtOff[ft] += rh;
    })
  );

  const rpath = (d: { srcTop: number; tgtTop: number; rh: number }) => {
    const x1 = lx + nodeW,
      x2 = rx;
    const sT = d.srcTop,
      sB = d.srcTop + d.rh;
    const tT = d.tgtTop,
      tB = d.tgtTop + d.rh;
    return `M${x1},${sT} C${midX},${sT},${midX},${tT},${x2},${tT}
            L${x2},${tB} C${midX},${tB},${midX},${sB},${x1},${sB}Z`;
  };

  const svg = select(svgEl);
  const rg = svg.select(".htft-ribbons");
  const ribs = rg
    .selectAll<SVGPathElement, RibbonDatum>(".htft-rib")
    .data(ribbonData, (d) => d.key);
  ribs
    .enter()
    .append("path")
    .attr("class", (d) => `htft-rib htft-rib-${d.ht.toLowerCase()} bento-fill`)
    .attr("fill", (d) => COLORS[d.ht])
    .attr("fill-opacity", 0.45)
    .attr("d", (d) =>
      rpath({
        rh: 0,
        srcTop: LL[d.ht].y + LL[d.ht].h / 2,
        tgtTop: RL[d.ft].y + RL[d.ft].h / 2,
      })
    )
    .merge(ribs)
    .transition()
    .duration(dur)
    .ease(ease)
    .attr("fill", (d) => COLORS[d.ht])
    .attr("d", (d) => rpath(d));
  ribs
    .exit<RibbonDatum>()
    .transition()
    .duration(dur)
    .ease(ease)
    .attr("fill-opacity", 0)
    .remove();

  // ---- Nodes ----
  const ng = svg.select(".htft-nodes");
  const ln = ng.selectAll<SVGRectElement, HtftResult>(".htft-ln").data(
    HTFT_ORDER,
    (d) => d
  );
  ln.enter()
    .append("rect")
    .attr("class", "htft-ln bento-fill")
    .attr("fill", (d) => COLORS[d])
    .attr("rx", 2)
    .attr("x", lx)
    .attr("width", nodeW)
    .attr("y", (d) => LL[d].y)
    .attr("height", 0)
    .merge(ln)
    .transition()
    .duration(dur)
    .ease(ease)
    .attr("x", lx)
    .attr("width", nodeW)
    .attr("y", (d) => LL[d].y)
    .attr("height", (d) => Math.max(0, LL[d].h));
  ln.exit().remove();

  const rn = ng.selectAll<SVGRectElement, HtftResult>(".htft-rn").data(
    HTFT_ORDER,
    (d) => d
  );
  rn.enter()
    .append("rect")
    .attr("class", "htft-rn bento-fill")
    .attr("fill", (d) => COLORS[d])
    .attr("rx", 2)
    .attr("x", rx)
    .attr("width", nodeW)
    .attr("y", (d) => RL[d].y)
    .attr("height", 0)
    .merge(rn)
    .transition()
    .duration(dur)
    .ease(ease)
    .attr("x", rx)
    .attr("width", nodeW)
    .attr("y", (d) => RL[d].y)
    .attr("height", (d) => Math.max(0, RL[d].h));
  rn.exit().remove();

  // ---- Labels ----
  const lg = svg.select(".htft-labels");
  const ll = lg.selectAll<SVGTextElement, HtftResult>(".htft-ll").data(
    HTFT_ORDER,
    (d) => d
  );
  ll.enter()
    .append("text")
    .attr("class", "htft-ll")
    .attr("text-anchor", "end")
    .style("font-size", "var(--t-label)")
    .attr("font-weight", "600")
    .attr("font-family", "inherit")
    .merge(ll)
    .attr("x", lx - 4)
    .attr("fill", (d) => COLORS[d])
    .transition()
    .duration(dur)
    .ease(ease)
    .attr("y", (d) => LL[d].y + LL[d].h / 2 + 3.5)
    .text((d) => (htC[d] > 0 ? `${d} ${htC[d]}` : ""));
  ll.exit().remove();

  const rl = lg.selectAll<SVGTextElement, HtftResult>(".htft-rl").data(
    HTFT_ORDER,
    (d) => d
  );
  rl.enter()
    .append("text")
    .attr("class", "htft-rl")
    .attr("text-anchor", "start")
    .style("font-size", "var(--t-label)")
    .attr("font-weight", "600")
    .attr("font-family", "inherit")
    .merge(rl)
    .attr("x", rx + nodeW + 4)
    .attr("fill", (d) => COLORS[d])
    .transition()
    .duration(dur)
    .ease(ease)
    .attr("y", (d) => RL[d].y + RL[d].h / 2 + 3.5)
    .text((d) => (ftC[d] > 0 ? `${d} ${ftC[d]}` : ""));
  rl.exit().remove();

  // ---- Column headers (created once, then positioned) ----
  let htHdr = svg.select<SVGTextElement>(".htft-ht-hdr");
  if (htHdr.empty())
    htHdr = svg
      .append("text")
      .attr("class", "htft-ht-hdr")
      .attr("text-anchor", "middle")
      .attr("fill", "#fff")
      .style("font-size", "var(--t-data)")
      .attr("font-weight", "600")
      .attr("font-family", "inherit");
  htHdr.attr("x", lx + nodeW / 2).attr("y", mT - 5).text("HT");

  let ftHdr = svg.select<SVGTextElement>(".htft-ft-hdr");
  if (ftHdr.empty())
    ftHdr = svg
      .append("text")
      .attr("class", "htft-ft-hdr")
      .attr("text-anchor", "middle")
      .attr("fill", "#fff")
      .style("font-size", "var(--t-data)")
      .attr("font-weight", "600")
      .attr("font-family", "inherit");
  ftHdr.attr("x", rx + nodeW / 2).attr("y", mT - 5).text("FT");
}

export interface HtFtFlowProps {
  counts: HtftCountData;
  total: number;
}

export default function HtFtFlow({ counts, total }: HtFtFlowProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const firstRef = useRef(true);
  const sig = JSON.stringify({ counts, total });

  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const useTransition = !firstRef.current;
    firstRef.current = false;
    draw(svgEl, counts, total, useTransition);

    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => draw(svgEl, counts, total, false));
    ro.observe(svgEl);
    return () => ro.disconnect();
    // `sig` captures counts + total (the complete draw input); read fresh.
  }, [sig]);

  return (
    <div className="data-wrapper htft-wrapper">
      <svg className="htft-svg" ref={svgRef}>
        <g className="htft-ribbons" />
        <g className="htft-nodes" />
        <g className="htft-labels" />
      </svg>
    </div>
  );
}
